import { useEffect, useState } from "react"

import type { BulkUpdateFormValues } from "@/screens/Pricing/schema/BulkUpdateSchema"
import { usePriceTableEditsStore } from "@/screens/Pricing/stores/usePriceTableEditsStore"
import type { PriceChangeSearchParams } from "@/screens/Pricing/types/searchParams"

interface UseBulkUpdateParams {
  searchParams: PriceChangeSearchParams
}

interface UseBulkUpdateReturn {
  appliedBulkUpdate: BulkUpdateFormValues | null
  pendingBulkUpdate: BulkUpdateFormValues | null
  handleBulkUpdateApply: (values: BulkUpdateFormValues) => void
  handleBulkUpdateConfirm: () => void
  handleBulkUpdateCancel: () => void
  handleBulkUpdateClear: () => void
  clearAppliedBulkUpdate: () => void
}

export const useBulkUpdate = ({ searchParams }: UseBulkUpdateParams): UseBulkUpdateReturn => {
  const [appliedBulkUpdate, setAppliedBulkUpdate] = useState<BulkUpdateFormValues | null>(null)
  const [pendingBulkUpdate, setPendingBulkUpdate] = useState<BulkUpdateFormValues | null>(null)
  const clearEdits = usePriceTableEditsStore((state) => state.clearEdits)
  const clearDefaultedRows = usePriceTableEditsStore((state) => state.clearDefaultedRows)
  const hasUnsavedEdits = usePriceTableEditsStore((s) => s.hasEdits)

  // Invalidate bulk update state when filters change (new result set makes prior selections stale) —
  // page is excluded since pagination shouldn't clear a bulk update.
  const filterKey = JSON.stringify({ ...searchParams, page: undefined })
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey)
    setAppliedBulkUpdate(null)
    setPendingBulkUpdate(null)
  }

  // No applied bulk update means nothing is defaulted.
  useEffect(() => {
    if (!appliedBulkUpdate) clearDefaultedRows()
  }, [appliedBulkUpdate, clearDefaultedRows])

  const applyBulkUpdate = (values: BulkUpdateFormValues) => {
    clearEdits()
    setAppliedBulkUpdate(values)
  }

  const handleBulkUpdateApply = (values: BulkUpdateFormValues) => {
    if (hasUnsavedEdits) {
      setPendingBulkUpdate(values)
    } else {
      applyBulkUpdate(values)
    }
  }

  const handleBulkUpdateConfirm = () => {
    if (!pendingBulkUpdate) return
    applyBulkUpdate(pendingBulkUpdate)
    setPendingBulkUpdate(null)
  }

  const handleBulkUpdateCancel = () => {
    setPendingBulkUpdate(null)
  }

  const handleBulkUpdateClear = () => {
    setAppliedBulkUpdate(null)
  }

  const clearAppliedBulkUpdate = () => {
    setAppliedBulkUpdate(null)
  }

  return {
    appliedBulkUpdate,
    pendingBulkUpdate,
    handleBulkUpdateApply,
    handleBulkUpdateConfirm,
    handleBulkUpdateCancel,
    handleBulkUpdateClear,
    clearAppliedBulkUpdate,
  }
}
